import { toast } from 'react-toastify';
import { JAVA_API_STATUS } from '../../../utils/axios-interceptor';
import { downloadBlobFromUrl } from '../../../utils/downloadUtils';
import { fetchBankStatementDataService, bsaGenerateService } from './service';
import { BSA_REPORT_DOWNLOAD_FAILED_MESSAGE, BSA_REPORT_GENERATION_FAILED_MESSAGE } from './constants';
import { BsaReportGenerateData, BsaHandlers, BsaGenerateHandlers } from './types';

export async function fetchBankStatementData(payload, handlers) {
    try {
        const response = await fetchBankStatementDataService();
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess(response.data?.bankData || {});
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        handlers?.onError();
        console.error('Error in fetching bank statement data', err);
    }
}

// Single blocking call: generates the report and hands the presigned URL back
// to the caller (which opens it in the in-app viewer) — UW only responds once
// the file is generated and uploaded, so there is no separate status/download
// round trip to make.
export async function generateBsaReport(investeeOrgId: string, handlers?: BsaGenerateHandlers) {
    try {
        const response = await bsaGenerateService(investeeOrgId);
        const data: BsaReportGenerateData = response?.data?.data;
        // Require both fields: the viewer opens on presignedUrl, but the drawer's
        // Download action needs fileName — treat a partial response as a failure
        // rather than opening a viewer whose Download button silently no-ops.
        if (!data?.presignedUrl || !data?.fileName) {
            handlers?.onError?.();
            toast.error(BSA_REPORT_GENERATION_FAILED_MESSAGE);
            return;
        }
        handlers?.onSuccess?.(data);
    } catch (err) {
        handlers?.onError?.();
        console.error('Error in generating bank statement analysis report', err);
        toast.error(BSA_REPORT_GENERATION_FAILED_MESSAGE);
    }
}

// Downloads a report directly from a presigned S3 URL: plain fetch (no auth headers) —
// mirrors the established presigned-download pattern (UploadCard/utils/api/helpers.ts downloadFile),
// not axios, since the interceptor attaches x-cerberus-token to every axios request by default.
export async function downloadBsaReportFile(presignedUrl: string, fileName: string, handlers?: BsaHandlers) {
    try {
        const res = await fetch(presignedUrl);
        if (res.status !== 200) {
            handlers?.onError?.();
            toast.error(BSA_REPORT_DOWNLOAD_FAILED_MESSAGE);
            return;
        }
        const blob = await res.blob();
        downloadBlobFromUrl(blob, fileName);

        handlers?.onSuccess?.();
        toast.success('Bank Statement Analysis Report downloaded successfully');
    } catch (err) {
        handlers?.onError?.();
        console.error('Error in downloading bank statement analysis report', err);
        toast.error(BSA_REPORT_DOWNLOAD_FAILED_MESSAGE);
    }
}
